import { Controller } from "@hotwired/stimulus";
import mapboxgl from "mapbox-gl";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import DrawRectangle from "mapbox-gl-draw-rectangle-mode";

export default class extends Controller {
  static values = {
    mapboxAccessToken: String,
    resolveUrl: String,
  };

  static targets = ["mapContainer", "areaGeo"];

  initialize() {
    mapboxgl.accessToken = this.mapboxAccessTokenValue;
    MapboxDraw.modes.draw_polygon = DrawRectangle;
  }

  async connect() {
    const centerUS = [-98.5795, 39.828175];

    this.map = new mapboxgl.Map({
      container: this.mapContainerTarget.id,
      center: centerUS,
      zoom: 3,
    });

    this.map.on("load", () => {
      this.draw = this.renderDraw();
      this.search = this.renderSearch();

      this.map.addControl(this.search);
      this.map.addControl(this.renderGeolocate(), "top-left");
      this.map.addControl(this.draw, "top-left");

      const drawFn = () => this.updateValue(this.draw.getAll().features[0]);

      this.map.on("draw.create", () => {
        drawFn();
        this.clearPolygons();
      });
      this.map.on("draw.update", drawFn);
      this.map.on("draw.delete", drawFn);

      if (this.geometryValue?.hasOwnProperty("coordinates")) {
        this.renderGeometry(this.geometryValue);
        this.zoomToGeometry(this.geometryValue);
      }

      this.search.addEventListener("retrieve", (event) => {
        this.selectGeometry(event);
      });
    });
  }

  get geometryValue() {
    return JSON.parse(this.areaGeoTarget.value || "{}");
  }

  updateValue(feature) {
    const geometry = feature?.geometry || {};
    this.areaGeoTarget.value = JSON.stringify(geometry);
  }

  renderDraw() {
    return new MapboxDraw({
      controls: {
        polygon: true,
        trash: true,
      },
      displayControlsDefault: false,
    });
  }

  renderGeolocate() {
    return new mapboxgl.GeolocateControl({
      positionOptions: {
        enableHighAccuracy: true,
      },
      fitBoundsOptions: {
        maxZoom: 11,
      },
    });
  }

  renderGeometry(geometry) {
    this.clearPolygons();
    this.draw.add(geometry);
  }

  renderSearch() {
    const search = new MapboxSearchBox();
    search.accessToken = this.mapboxAccessTokenValue;
    search.options = {
      types: ["country", "district", "place", "postcode"],
    };

    return search;
  }

  async selectGeometry(event) {
    const feature = event.detail.features[0];
    const props = feature.properties;

    const query = props.full_address || props.name;
    const areaType = props.feature_type === "postcode" ? "zip_code" : "city";

    const geometry = await this.fetchBoundary(query, areaType);
    if (geometry) {
      this.renderGeometry(geometry);
      this.updateValue({ geometry });
      this.zoomToGeometry(geometry);
    }
  }

  async fetchBoundary(query, areaType) {
    try {
      const response = await fetch(this.resolveUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
            .content,
        },
        body: JSON.stringify({ query, area_type: areaType }),
      });

      if (!response.ok) return null;

      const data = await response.json();
      return data.geometry;
    } catch (error) {
      console.error("Failed to resolve boundary:", error);
      return null;
    }
  }

  zoomToGeometry(geometry) {
    const coords =
      geometry.type === "Polygon"
        ? geometry.coordinates.flat()
        : geometry.coordinates.flat(2);
    const bounds = coords.reduce(function (bounds, coord) {
      return bounds.extend(coord);
    }, new mapboxgl.LngLatBounds());

    this.map.fitBounds(bounds, {
      maxZoom: 15,
      padding: 50,
    });
  }

  clearPolygons() {
    const data = this.draw.getAll();
    let pids = [];
    const persistId =
      this.draw.getMode() == "draw_polygon"
        ? data.features[data.features.length - 1].id
        : null;

    data.features.forEach((f) => {
      if (f.id !== persistId) pids.push(f.id);
    });

    this.draw.delete(pids);
  }
}
